import { useEffect, useState } from 'react';
import { Alert, Button, Form, Input, Switch, Tag } from 'antd';
import {
  SafetyCertificateOutlined,
  QrcodeOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import endpoints from '../../api/endpoints';
import { useAppNotifications, parseApiError } from '../../utils/notify';
import LoadingBlock from '../../components/LoadingBlock';

// Stage 5M: Master-only TOTP setup section. Mounted inside
// UserDetailsPanel under "Access & security" when the panel is
// rendering the master's own profile (selfMode + viewer is master).
//
// SECURITY: this component shows the QR code and the manual entry key
// only inside this surface. The code is sensitive — anyone who scans
// the QR or types the manual key into Authenticator can generate the
// shared TOTP code that all sub-users with mfa_required=true will use
// at login.

export default function MasterMfaSection({ onChanged }) {
  const { notifyError, notifySuccess } = useAppNotifications();

  const [statusLoading, setStatusLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  // Setup-in-progress UI state. The QR + manual key live ONLY in
  // memory; we never persist them client-side.
  const [setupData, setSetupData] = useState(null);
  const [setupCode, setSetupCode] = useState('');

  async function loadStatus() {
    setStatusLoading(true);
    try {
      const data = await endpoints.mfa.getStatus();
      setStatus(data);
    } catch (err) {
      notifyError(err, { title: 'Could not load MFA status' });
    } finally {
      setStatusLoading(false);
    }
  }

  useEffect(() => {
    loadStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function startSetup() {
    setBusy(true);
    try {
      const data = await endpoints.mfa.beginSetup();
      setSetupData(data);
      setSetupCode('');
    } catch (err) {
      notifyError(err, { title: 'Could not start MFA setup' });
    } finally {
      setBusy(false);
    }
  }

  async function verifySetup() {
    if (!/^\d{6}$/.test(setupCode)) {
      notifyError(
        { code: 'MFA_INVALID_CODE' },
        { title: 'Enter the 6-digit code' }
      );
      return;
    }
    setBusy(true);
    try {
      await endpoints.mfa.verifySetup({ code: setupCode });
      notifySuccess(
        'MFA enabled',
        'You can now require MFA for sub-users from each user profile.'
      );
      setSetupData(null);
      setSetupCode('');
      await loadStatus();
      if (onChanged) onChanged();
    } catch (err) {
      const parsed = parseApiError(err);
      // The 10-min setup window elapsed before the code was entered, so
      // the staged secret is gone. Verifying this QR would fail forever
      // against the previous secret — instead surface the reason and
      // stage a fresh secret so a NEW QR appears for an immediate retry.
      if (parsed && parsed.code === 'MFA_SETUP_EXPIRED') {
        notifyError(err);
        setSetupCode('');
        await startSetup();
        return;
      }
      notifyError(err, { title: 'MFA verification failed' });
    } finally {
      setBusy(false);
    }
  }

  function cancelSetup() {
    setSetupData(null);
    setSetupCode('');
  }

  async function toggleActive(nextActive) {
    setBusy(true);
    try {
      await endpoints.mfa.setActive({ isActive: nextActive });
      notifySuccess(
        nextActive ? 'MFA active' : 'MFA disabled',
        nextActive
          ? 'Sub-users marked as MFA-required will be asked for the 6-digit code at login.'
          : 'MFA enforcement is paused. Existing per-user settings are preserved.'
      );
      await loadStatus();
      if (onChanged) onChanged();
    } catch (err) {
      notifyError(err, { title: 'Could not change MFA active state' });
    } finally {
      setBusy(false);
    }
  }

  // ---------- render --------------------------------------------------
  return (
    <div className="mt-4 rounded-xl border border-surface-200 bg-white p-3.5">
      <div className="flex items-center gap-2 mb-2">
        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-brand-50 text-brand-600 shrink-0">
          <SafetyCertificateOutlined />
        </span>
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-ink-900 m-0 leading-tight">
            Master MFA setup
          </h3>
          <p className="text-xs text-ink-500 m-0 mt-0.5">
            One Google Authenticator setup for the whole account. The
            current code is shared with sub-users you mark as MFA-required.
          </p>
        </div>
      </div>

      {statusLoading || !status ? (
        <LoadingBlock />
      ) : setupData ? (
        // -------- Setup-in-progress branch ----------------------------
        <div>
          <Alert
            type="warning"
            showIcon
            message="Delete any old “Glass-i” entry in your Authenticator first"
            description={
              <span>
                Each time you re-scan, your app adds a <strong>new</strong> entry.
                If an older <strong>{setupData.accountLabel || 'Glass-i'}</strong> entry
                is still there, its code will be rejected. Remove old Glass-i
                entries, then scan the QR below and type the code from the
                <strong> newly added</strong> entry.
              </span>
            }
            className="mb-3"
          />
          <Alert
            type="info"
            showIcon
            message="Scan the QR with Google Authenticator, then enter the current 6-digit code (before it refreshes)."
            className="mb-3"
          />
          <div className="flex flex-col sm:flex-row gap-4 items-start">
            <div className="rounded-lg border border-surface-200 p-2 bg-white shrink-0">
              <img
                src={setupData.qrDataUrl}
                alt="MFA QR code"
                className="block w-[200px] h-[200px]"
              />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="text-xs uppercase tracking-wider text-ink-500 font-semibold m-0 mb-1.5">
                Manual entry key
              </h4>
              <code className="block text-sm font-mono bg-surface-50 border border-surface-200 rounded p-2 break-all">
                {setupData.manualEntryKey}
              </code>
              <p className="text-xs text-ink-500 mt-2 mb-3">
                Issuer: <strong>{setupData.issuer}</strong>
                <br />
                Account: <strong>{setupData.accountLabel}</strong>
              </p>
              <Form layout="vertical">
                <Form.Item
                  label={
                    <span className="text-sm font-semibold text-ink-800">
                      6-digit code
                    </span>
                  }
                  required
                  className="!mb-2"
                >
                  <Input
                    placeholder="123456"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={setupCode}
                    onChange={(e) => {
                      const next = String(e.target.value || '')
                        .replace(/\D/g, '')
                        .slice(0, 6);
                      setSetupCode(next);
                    }}
                    onPressEnter={verifySetup}
                  />
                </Form.Item>
                <div className="flex gap-2">
                  <Button
                    type="primary"
                    icon={<CheckCircleOutlined />}
                    loading={busy}
                    disabled={busy || setupCode.length !== 6}
                    onClick={verifySetup}
                  >
                    Verify and enable MFA
                  </Button>
                  <Button onClick={cancelSetup} disabled={busy}>
                    Cancel
                  </Button>
                </div>
              </Form>
            </div>
          </div>
        </div>
      ) : (
        // -------- Status branch --------------------------------------
        <div>
          <div className="flex items-center justify-between gap-3 px-1 py-1">
            <div className="flex items-center gap-2 flex-wrap">
              {status.isSetupComplete ? (
                <Tag color="green">Configured</Tag>
              ) : (
                <Tag>Not configured</Tag>
              )}
              {status.isSetupComplete ? (
                status.isActive ? (
                  <Tag color="blue">Active</Tag>
                ) : (
                  <Tag color="default">Disabled</Tag>
                )
              ) : null}
            </div>
            <div className="flex items-center gap-3">
              {status.isSetupComplete ? (
                <span className="flex items-center gap-2 text-sm text-ink-700">
                  <span>Active</span>
                  <Switch
                    checked={status.isActive}
                    loading={busy}
                    onChange={toggleActive}
                  />
                </span>
              ) : null}
              <Button
                type={status.isSetupComplete ? 'default' : 'primary'}
                icon={<QrcodeOutlined />}
                loading={busy}
                onClick={startSetup}
              >
                {status.isSetupComplete ? 'Re-scan QR' : 'Setup MFA'}
              </Button>
            </div>
          </div>

          {!status.isSetupComplete ? (
            <p className="text-xs text-ink-500 m-0 mt-2">
              Scan the QR once on the master's phone. Sub-user MFA cannot be
              required until the setup is verified.
            </p>
          ) : !status.isActive ? (
            <Alert
              type="warning"
              showIcon
              className="mt-3"
              message="MFA is configured but inactive."
              description="Sub-users marked as MFA-required can still sign in with just password until you re-activate MFA."
            />
          ) : null}
        </div>
      )}
    </div>
  );
}
